import React, { Fragment, useEffect } from "react";
import DarkWebScanWidget from "./DarkWebScanWidget";
import * as NextStepFlowAction from "../../store/nextStepFlowService/actions";
import * as MonitorActions from "../../store/monitorService/actions";
import * as TransactionActions from "../../store/transaction/actions";
import NextStep from "./NextStep";
import CreditAuthentication from "./CreditAuthentication";
import { flowName } from "../../config/FlowName";
import { ApplicationState } from "../../store";
import { useSelector, useDispatch } from "react-redux";
import ActiveCreditMonitoring from "./ActiveCreditMonitoring";
import { enableMXForIDF, ezsServiceIds, idfServiceIds } from "../../config/UserServicesList";
import { useAreServicesActive } from "../../util/CommonServices";
import ErrorBoundary from "../common/ErrorBoundary";

const DashboardMessages = () => {
    const dispatch = useDispatch();
    const requestNextStepFlow = () => dispatch(NextStepFlowAction.actionCreators.requestNextStepFlow());
    const requestAccountSummary = () => dispatch(TransactionActions.actionCreators.requestAccountSummary());

    
    const requestMonitors = () => dispatch(MonitorActions.actionCreators.requestMonitors(false));
    const yodleeServiceList = [idfServiceIds.bankAndCreditCardActivity, ezsServiceIds.financialTransactionMonitoring];
    const isYodleeTransactionServiceActive = useAreServicesActive(yodleeServiceList) && !enableMXForIDF;
    const isYodleeServiceEnabled = useAreServicesActive(yodleeServiceList) && enableMXForIDF;
    const mxServiceList = [ezsServiceIds.financialTransactionMonitoringMX] ;
    const ismxServiceActive = useAreServicesActive(mxServiceList) || isYodleeServiceEnabled;
    
    
    const totalConnectedFinancialAccount = useSelector((state: ApplicationState) =>
        state.accountSummaryData && state.accountSummaryData.accountSummaryData && state.accountSummaryData.accountSummaryData.data
            ? state.accountSummaryData.accountSummaryData.data.length
            : 0
    );
    const hasDisconnectedAccount = useSelector(
        (state: ApplicationState) =>
            state.accountSummaryData &&
            state.accountSummaryData.accountSummaryData &&
            state.accountSummaryData.accountSummaryData.data &&
            state.accountSummaryData.accountSummaryData.data.filter((data: any) => data.isDisconnected).length > 0
    );
    const isFinancialAccountDataLoading = useSelector((state: ApplicationState) => state.accountSummaryData.isLoading);
    const isSocialMediaMonirotingInfoLoading = useSelector((state: ApplicationState) =>
        state.monitorData && state.monitorData.isLoading ? state.monitorData.isLoading : false
    );
    const socialMediaMonitorData = useSelector((state: ApplicationState) =>
        state.monitorData && state.monitorData.monitorData ? state.monitorData.monitorData : []
    );
    const flows = useSelector((state: ApplicationState) => (state.nextStepFlowState && state.nextStepFlowState.flows ? state.nextStepFlowState.flows : []));
    const refreshingCSIDUserStatus = useSelector((state: ApplicationState) =>
        state.csidUserStatusState && state.csidUserStatusState.refreshingCSIDUserStatus ? state.csidUserStatusState.refreshingCSIDUserStatus : false
    );
    
    const isSocialMediaMonitoringActive = useAreServicesActive([idfServiceIds.socialMediaMonitoring, ezsServiceIds.socialMediaMonitoring]);
    const creditScoreAndReportServiceList = [
        ezsServiceIds.quarterlyCreditScore,
        ezsServiceIds.tuDailyCreditScore,
        ezsServiceIds.tuMonthlyCreditScore,
        ezsServiceIds.tuQuarterlyCreditScore,
        ezsServiceIds.tuAnnualCreditScore,
        ezsServiceIds.tuCanadaCreditScore,
        ezsServiceIds.monthlyCreditScore,
        ezsServiceIds.oneBureauQuarterlyCreditReport,
        ezsServiceIds.oneBureauMonthlyCreditReport,
        // ezsServiceIds.tuCreditReportWithScore,
        ezsServiceIds.tuDailyCreditReportWithScore,
        ezsServiceIds.tuMonthlyCreditReportWithScore,
        ezsServiceIds.tuQuarterlyCreditReportWithScore,
        ezsServiceIds.tuAnnualCreditReportWithScore,
        ezsServiceIds.experianAnnualCreditReportWithScore,
        ezsServiceIds.tuCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuMonthlyCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuQuarterlyCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuAnnualCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.experianAnnualCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuCanadaMonthlyCreditReportWithScore,
        ezsServiceIds.tuCanadaQuarterlyCreditReportWithScore,
        ezsServiceIds.tuCanadaAnnualCreditReportWithScore,
        ezsServiceIds.tuCanadaOneTimeCreditReportWithScore,
    ];
    const isCreditScoreOrReportctive = useAreServicesActive(creditScoreAndReportServiceList);

    useEffect(() => {
        requestMonitors();
        requestNextStepFlow();
        if(ismxServiceActive || isYodleeTransactionServiceActive){
            requestAccountSummary();
        }
    }, []);

    const isNextStepsExists = (flowName: string) => {
        let stepNameFound = false;
        if (flows.length > 0) {
            const search = (flowName: string) => flows.find((element) => element["flowName"] === flowName);
            if (search(flowName)) {
                stepNameFound = true;
            } else {
                stepNameFound = false;
            }
        }

        return stepNameFound;
    };

    const getNextStepFlowName = (flowName: string) => {
        let stepFlowName = "";
        if (flows.length > 0) {
            const search = flows.find((element) => element["flowName"] === flowName);
            if (search) {
                stepFlowName = search["flowStepName"] as string;
            } else {
                stepFlowName = "";
            }
        }

        return stepFlowName;
    };

    const showFinancialAccountMessage = () => {
        if (
            isYodleeTransactionServiceActive &&
            isNextStepsExists(flowName.dashboardReconnectFinancialAccount) &&
            !isFinancialAccountDataLoading &&
            totalConnectedFinancialAccount <= 0
        ) {
            return true;
        }
        return false;
    };

    const showSocialMediaAccountMessage = () => {
        let isAnyAccountConnected = false;

        if (
            isSocialMediaMonitoringActive &&
            isNextStepsExists(flowName.dashboardReconnectSocialMedia) &&
            !isSocialMediaMonirotingInfoLoading &&
            !isAnyAccountConnected
        ) {
            return true;
        }

        return false;
    };

    return (
        <Fragment>
            <div id="alerts" role="alert" aria-labelledby="alerts-title">
                <h2 className="sr-only" id="alerts-title">
                    Alerts
                </h2>

                <ul id="alert-list">
                    <ErrorBoundary componentName="DashboardAlerts" type="hideError" replaceDefaultContent={false} errorMsg="">
                        <CreditAuthentication />
                    </ErrorBoundary>

                    {showFinancialAccountMessage() && <NextStep flowStepName="Reconnect your Financial Account" navigationPath="/transactions/financial-account" />}
                    {showSocialMediaAccountMessage() && (
                        <NextStep flowStepName="Reconnect your Social Media Account" navigationPath="/identity-vault?focus=socialMedia" />
                    )}
                    {hasDisconnectedAccount && <NextStep flowStepName="Reconnect your Financial Account" navigationPath="/transactions/financial-account" />}
                    {isNextStepsExists(flowName.dashboardSSNPinningRequired) && !isCreditScoreOrReportctive && !refreshingCSIDUserStatus && (
                        <ActiveCreditMonitoring flowStepName={getNextStepFlowName(flowName.dashboardSSNPinningRequired)} />
                    )}
                </ul>
            </div>

            {isNextStepsExists(flowName.dashboardDarkwebScan) && <DarkWebScanWidget />}
        </Fragment>
    );
};

export default DashboardMessages;
